import { useMemo } from 'react';
import type {
  CatalogDeliveryDto,
  CatalogDeliveryPrepareDto,
  CatalogDeliverySignedUpload,
  ChecklistCatalogDeliveryManifest,
  ChecklistCatalogImpactBaselineDto,
  ChecklistCatalogImpactReportDto,
} from '@tcvs/shared';
import { useApi } from '../api';
import type { ApiRequestOptions, ApiResult } from '../auth/api-client';
type Client = { request<T>(path: string, options?: ApiRequestOptions): Promise<ApiResult<T>> };
const path = '/checklists/deliveries';
export function createCatalogDeliveryApi(client: Client) {
  return {
    baseline: (sourceTargetId: string) =>
      client.request<ChecklistCatalogImpactBaselineDto>(path, {
        method: 'POST',
        body: { action: 'baseline', sourceTargetId },
      }),
    prepare: (manifest: ChecklistCatalogDeliveryManifest) =>
      client.request<CatalogDeliveryPrepareDto>(path, {
        method: 'POST',
        body: { action: 'prepare', manifest },
      }),
    seal: (deliveryId: string, sourceTargetId: string) =>
      client.request<CatalogDeliveryDto>(path, {
        method: 'POST',
        body: { action: 'seal', deliveryId, sourceTargetId },
      }),
    validate: (deliveryId: string, sourceTargetId: string, requestId: string) =>
      client.request<ChecklistCatalogImpactReportDto>(path, {
        method: 'POST',
        body: { action: 'validate', deliveryId, sourceTargetId, requestId },
      }),
    report: (deliveryId: string, sourceTargetId: string, reportId: string) =>
      client.request<ChecklistCatalogImpactReportDto>(path, {
        method: 'POST',
        body: { action: 'report', deliveryId, sourceTargetId, reportId },
      }),
  };
}
export function useCatalogDeliveryApi() {
  const api = useApi();
  return useMemo(() => createCatalogDeliveryApi(api), [api]);
}
export async function uploadCatalogDeliveryFile(
  upload: CatalogDeliverySignedUpload,
  bytes: Uint8Array,
): Promise<void> {
  // Returned headers bind the exact checksum/length and conditional creation. No bearer token goes to S3.
  const response = await fetch(upload.url, {
    method: upload.method,
    credentials: 'omit',
    headers: upload.headers,
    body: new Blob([bytes]),
  });
  if (!response.ok && response.status !== 412)
    throw new Error('Upload failed. Resume to retry this product.');
  // 412 means a conditional object already exists; seal still verifies all actual stored bytes.
}
